import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';

import { usersApi } from './users';
import { trackFirstTrackingLogged } from '../../utils/analytics';

/**
 * Shared by create and update: both write a value for one field on one day.
 *
 * Every surface that logs anything goes through one of the two, which is the
 * only reason the activation event can be relied on — wiring it into callers
 * is how water and weight came to be missing from it.
 */
async function onTrackingWritten(arg, { dispatch, queryFulfilled }) {
  try {
    await queryFulfilled;
  } catch {
    // A failed write is not an activation, and the caller reports the error.
    return;
  }

  trackFirstTrackingLogged(arg.field);

  if (arg.field === 'weight') {
    dispatch(usersApi.util.invalidateTags(['Profile']));
  }
}

export const trackingsApi = createApi({
  reducerPath: 'trackingsApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getTrackings: builder.query({
      query: (filters = {}) => ({
        url: 'trackings',
        method: 'GET',
        params: filters,
      }),
      providesTags: ['Trackings'],
    }),
    createTracking: builder.mutation({
      query: (newTracking) => ({
        url: 'trackings',
        method: 'POST',
        body: newTracking,
      }),
      invalidatesTags: ['Trackings'],
      onQueryStarted: onTrackingWritten,
    }),
    updateTracking: builder.mutation({
      query: (data) => ({
        url: 'trackings',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Trackings'],
      onQueryStarted: onTrackingWritten,
    }),
    deleteTrackingField: builder.mutation({
      query: (data) => ({
        url: 'trackings/field',
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['Trackings'],
    }),
    deleteTracking: builder.mutation({
      query: ({ userId, date }) => ({
        url: 'trackings',
        method: 'DELETE',
        params: { userId, date },
      }),
      invalidatesTags: ['Trackings'],
    }),

    addExerciseLog: builder.mutation({
      query: (data) => ({
        url: 'trackings/exercise-log',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Trackings'],
    }),

    recordWorkout: builder.mutation({
      query: (data) => ({
        url: 'user-workouts',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Trackings'],
    }),
  }),
});

export const {
  useGetTrackingsQuery,
  useCreateTrackingMutation,
  useUpdateTrackingMutation,
  useDeleteTrackingFieldMutation,
  useDeleteTrackingMutation,
  useAddExerciseLogMutation,
  useRecordWorkoutMutation,
} = trackingsApi;
